<template>
  <v-container fluid class="tv-main-container">
    <loader :loading="loading"></loader>
    <div class="tv-breadcrumbs-container">
      <Breadcrumbs />
    </div>

    <!-- Hero Guide Card -->
    <v-card class="tv-hero-card" elevation="3">
      <div class="tv-hero-header">
        <div class="tv-hero-header-content">
          <div class="tv-hero-logo">
            <v-icon color="white" size="30">mdi-file-tree</v-icon>
          </div>
          <div>
            <div class="tv-hero-title">Security Assurance Case Tree View</div>
            <div class="tv-hero-subtitle">
              Navigate and manage your hierarchical security assurance structure
            </div>
          </div>
        </div>
      </div>
      <div class="tv-guide-body">
        <v-row>
          <v-col cols="12" md="6">
            <div class="tv-info-card">
              <div class="tv-info-icon-wrapper nav-icon">
                <v-icon color="white" size="22">mdi-file-tree</v-icon>
              </div>
              <div class="tv-info-title">Navigating the Tree</div>
              <ul class="tv-info-list">
                <li>
                  <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>Click
                  triangles to expand or collapse sections
                </li>
                <li>
                  <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>Use
                  <b>Expand All</b> / <b>Collapse All</b> for quick navigation
                </li>
                <li>
                  <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>Select items
                  to view full details in the side panel
                </li>
                <li>
                  <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>Items are
                  color-coded: <v-chip x-small color="primary" dark class="mx-1">Claims</v-chip>
                  <v-chip x-small color="secondary" dark class="mx-1">Sub-Claims</v-chip>
                  <v-chip x-small color="success" dark class="mx-1">Evidence</v-chip>
                </li>
              </ul>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="tv-info-card">
              <div class="tv-info-icon-wrapper manage-icon">
                <v-icon color="white" size="22">mdi-pencil-plus</v-icon>
              </div>
              <div class="tv-info-title">Managing Tree Items</div>
              <ul class="tv-info-list">
                <li>
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>Add claims at
                  the root level with <v-icon x-small color="primary">mdi-plus</v-icon>
                </li>
                <li>
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>Add sub-claims
                  to claims with <v-icon x-small color="secondary">mdi-plus</v-icon>
                </li>
                <li>
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>Add evidence
                  to sub-claims with <v-icon x-small color="success">mdi-plus</v-icon>
                </li>
                <li>
                  <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>Edit with
                  <v-icon x-small>mdi-pencil</v-icon> or delete with
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>

        <!-- Workflow Tips -->
        <div class="tv-tips-bar mt-4">
          <v-icon color="amber darken-2" class="mr-2">mdi-lightbulb-on</v-icon>
          <span class="font-weight-bold mr-4">Tips:</span>
          <v-chip small outlined color="amber darken-2" class="mr-2"
            ><v-icon left x-small>mdi-star</v-icon>Start with top-level claims</v-chip
          >
          <v-chip small outlined color="amber darken-2" class="mr-2"
            ><v-icon left x-small>mdi-star</v-icon>Break into verifiable sub-claims</v-chip
          >
          <v-chip small outlined color="amber darken-2" class="mr-2"
            ><v-icon left x-small>mdi-star</v-icon>Attach supporting evidence</v-chip
          >
          <v-chip small outlined color="amber darken-2"
            ><v-icon left x-small>mdi-star</v-icon>Export to share</v-chip
          >
        </div>
      </div>
    </v-card>

    <!-- Back Navigation -->
    <v-btn
      class="tv-back-btn"
      outlined
      color="primary"
      @click="$router.push({ name: 'SacBuilder' })"
    >
      <v-icon left>mdi-arrow-left</v-icon>
      Back to Builder
    </v-btn>

    <!-- Tree Header Card -->
    <v-card class="tv-tree-header-card" elevation="2">
      <div class="tv-tree-header-gradient">
        <div class="d-flex align-center">
          <v-icon color="white" size="26" class="mr-3">mdi-file-tree</v-icon>
          <div>
            <span class="white--text text-h6 font-weight-bold"
              >Security Assurance Case Structure</span
            >
            <div class="d-flex align-center mt-1">
              <span class="white--text text-body-2 mr-2" style="opacity: 0.85;">{{
                requirementTitle
              }}</span>
              <v-chip x-small dark color="rgba(255,255,255,0.25)" class="font-weight-bold">{{
                requirementId
              }}</v-chip>
            </div>
          </div>
        </div>
        <div class="d-flex">
          <v-btn dark text class="tv-toolbar-btn" @click="expandAll">
            <v-icon left small>mdi-arrow-expand-all</v-icon>
            Expand All
          </v-btn>
          <v-btn dark text class="tv-toolbar-btn" @click="collapseAll">
            <v-icon left small>mdi-arrow-collapse-all</v-icon>
            Collapse All
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-row>
      <v-col cols="12" lg="9">
        <!-- Tree View Panel -->
        <v-card class="tv-tree-card" elevation="2">
          <div class="tv-tree-card-header">
            <v-icon color="white" size="20" class="mr-2">mdi-sitemap</v-icon>
            <span>Tree Structure</span>
            <v-spacer></v-spacer>
            <v-chip small dark color="rgba(255,255,255,0.2)">
              {{ items.length ? (items[0].children ? items[0].children.length : 0) : 0 }} Claims
            </v-chip>
          </div>
          <v-card-text class="px-0 py-0">
            <v-treeview
              v-if="items.length > 0"
              :items="items"
              item-text="name"
              item-children="children"
              :open.sync="openNodes"
              hoverable
              rounded
              dense
              activatable
              @update:active="nodeSelected"
              class="tv-modern-treeview"
            >
              <template v-slot:prepend="{ item }">
                <div class="tv-node-icon-wrapper" :class="'tv-node-icon--' + item.type">
                  <v-icon v-if="item.type === 'root'" size="20" color="white">mdi-file-tree</v-icon>
                  <v-icon v-else-if="item.type === 'claim'" size="20" color="white"
                    >mdi-clipboard-text-outline</v-icon
                  >
                  <v-icon v-else-if="item.type === 'subClaim'" size="20" color="white"
                    >mdi-format-list-checks</v-icon
                  >
                  <v-icon v-else-if="item.type === 'evidence'" size="20" color="white"
                    >mdi-file-document-outline</v-icon
                  >
                </div>
              </template>

              <template v-slot:label="{ item }">
                <div class="d-flex align-center tree-node">
                  <div class="d-flex flex-column flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="font-weight-medium tree-item-title">{{ item.name }}</span>
                      <v-chip
                        x-small
                        :color="getStatusColor(item.status)"
                        text-color="white"
                        class="ml-2"
                        v-if="item.status"
                      >
                        {{ item.status }}
                      </v-chip>

                      <v-chip
                        v-if="item.type === 'claim' && item.childCount !== undefined"
                        x-small
                        outlined
                        color="primary"
                        class="ml-2"
                      >
                        {{ item.childCount }} Sub-Claims
                      </v-chip>

                      <v-chip
                        v-if="item.type === 'subClaim' && item.childCount !== undefined"
                        x-small
                        outlined
                        color="secondary"
                        class="ml-2"
                      >
                        {{ item.childCount }} Evidence
                      </v-chip>
                    </div>

                    <div
                      class="tree-item-description text-caption text-truncate"
                      v-if="item.description"
                    >
                      {{ item.description }}
                    </div>
                  </div>

                  <div class="tree-actions">
                    <v-tooltip bottom v-if="item.type === 'root'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="openAddClaimDialog(item)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>Add New Claim</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.type === 'claim'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="secondary"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="openAddSubClaimDialog(item)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>Add New Sub-Claim</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.type === 'subClaim'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="success"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="openAddEvidenceDialog(item)"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>Add New Evidence</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.type !== 'root'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="g-action-btn g-action-edit"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="openEditDialog(item)"
                        >
                          <v-icon x-small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="item.type !== 'root'">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          class="g-action-btn g-action-delete"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="confirmDelete(item)"
                        >
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>
                  </div>
                </div>
              </template>
            </v-treeview>

            <v-alert v-else type="info" class="ma-4">
              No claims found. Click the button below to create your first claim.
              <div class="text-center mt-2">
                <v-btn color="primary" @click="openAddClaimDialog({ type: 'root', id: 'root' })">
                  <v-icon left>mdi-plus</v-icon>
                  Add First Claim
                </v-btn>
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="3">
        <!-- Details Panel -->
        <v-card v-if="selectedNode" class="tv-detail-card" elevation="2">
          <div class="tv-detail-header" :class="'tv-detail-header--' + selectedNode.type">
            <v-icon color="white" size="22" class="mr-2">{{
              getNodeIcon(selectedNode.type)
            }}</v-icon>
            <span>{{ getNodeTypeLabel(selectedNode.type) }} Details</span>
          </div>
          <v-card-text class="pt-4">
            <div class="tv-detail-item">
              <div class="tv-detail-label">Name</div>
              <div class="tv-detail-value">{{ selectedNode.name }}</div>
            </div>
            <div class="tv-detail-item">
              <div class="tv-detail-label">Status</div>
              <v-chip small :color="getStatusColor(selectedNode.status)" text-color="white">
                {{ selectedNode.status || 'N/A' }}
              </v-chip>
            </div>
            <div class="tv-detail-item">
              <div class="tv-detail-label">Description</div>
              <div class="tv-detail-value">
                {{ selectedNode.description || 'No description available' }}
              </div>
            </div>

            <!-- Exclaim Scorer Metrics for Claims -->
            <template v-if="selectedNode.type === 'claim' && hasExclaimScores(selectedNode)">
              <v-divider class="my-3"></v-divider>
              <div class="tv-scoring-metrics">
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" small class="mr-2">mdi-check-decagram</v-icon>
                  <span class="font-weight-bold text-subtitle-2">Exclaim Scores</span>
                </div>

                <div class="tv-detail-item">
                  <div class="tv-detail-label mb-1">Comprehensiveness</div>
                  <v-progress-linear
                    :value="selectedNode.comprehensivenessScore * 100"
                    :color="getScoreColorFromValue(selectedNode.comprehensivenessScore)"
                    height="22"
                    rounded
                    striped
                  >
                    <template v-slot:default>
                      <strong class="white--text text-caption"
                        >{{ (selectedNode.comprehensivenessScore * 100).toFixed(1) }}%</strong
                      >
                    </template>
                  </v-progress-linear>
                </div>

                <div class="tv-detail-item">
                  <div class="tv-detail-label mb-1">Sufficiency</div>
                  <v-progress-linear
                    :value="selectedNode.sufficiencyScore * 100"
                    :color="getScoreColorFromValue(selectedNode.sufficiencyScore)"
                    height="22"
                    rounded
                    striped
                  >
                    <template v-slot:default>
                      <strong class="white--text text-caption"
                        >{{ (selectedNode.sufficiencyScore * 100).toFixed(1) }}%</strong
                      >
                    </template>
                  </v-progress-linear>
                </div>

                <div
                  class="tv-detail-item"
                  v-if="selectedNode.score !== null && selectedNode.score !== undefined"
                >
                  <div class="tv-detail-label mb-1">Overall Score</div>
                  <div class="text-center">
                    <v-progress-circular
                      :value="selectedNode.score"
                      :color="getScoreColorFromValue(selectedNode.score / 100)"
                      :size="70"
                      :width="7"
                      class="font-weight-bold tv-score-circle"
                    >
                      {{ selectedNode.score }}%
                    </v-progress-circular>
                  </div>
                </div>

                <div class="tv-detail-item" v-if="selectedNode.scoredAt">
                  <div class="tv-detail-label">Scored At</div>
                  <div class="text-caption">{{ formatDate(selectedNode.scoredAt) }}</div>
                </div>

                <div class="tv-detail-item" v-if="selectedNode.scoringPremise">
                  <div class="tv-detail-label">Scoring Premise</div>
                  <div class="text-caption">{{ selectedNode.scoringPremise }}</div>
                </div>
              </div>
            </template>

            <v-divider
              class="my-3"
              v-if="selectedNode.createdAt || selectedNode.updatedAt"
            ></v-divider>

            <div class="tv-detail-item" v-if="selectedNode.createdAt">
              <div class="tv-detail-label">Created</div>
              <div class="tv-detail-value text-caption">
                {{ formatDate(selectedNode.createdAt) }}
              </div>
            </div>
            <div class="tv-detail-item" v-if="selectedNode.updatedAt">
              <div class="tv-detail-label">Last Updated</div>
              <div class="tv-detail-value text-caption">
                {{ formatDate(selectedNode.updatedAt) }}
              </div>
            </div>
            <div class="tv-detail-item" v-if="selectedNode.username">
              <div class="tv-detail-label">Author</div>
              <div class="tv-detail-value">{{ selectedNode.username }}</div>
            </div>
            <div class="tv-detail-item" v-if="selectedNode.evidenceType">
              <div class="tv-detail-label">Evidence Type</div>
              <v-chip small color="success" outlined>{{ selectedNode.evidenceType }}</v-chip>
            </div>
            <div class="tv-detail-item" v-if="selectedNode.source">
              <div class="tv-detail-label">Source</div>
              <v-chip small :color="getSourceColor(selectedNode.source)" outlined>{{
                selectedNode.source
              }}</v-chip>
            </div>

            <v-btn
              color="primary"
              block
              class="mt-4 tv-edit-btn"
              @click="openEditDialog(selectedNode)"
            >
              <v-icon left>mdi-pencil</v-icon>
              Edit Details
            </v-btn>
          </v-card-text>
        </v-card>

        <v-card v-else class="tv-detail-card tv-detail-empty" elevation="1">
          <div class="tv-detail-header tv-detail-header--empty">
            <v-icon color="white" size="22" class="mr-2">mdi-cursor-default-click</v-icon>
            <span>Details Panel</span>
          </div>
          <v-card-text class="text-center pa-8">
            <v-icon size="48" color="grey lighten-1">mdi-cursor-default-click-outline</v-icon>
            <div class="mt-3 text-body-2 grey--text">
              Select an item from the tree to view its details
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Action Button Row -->
    <v-card class="tv-action-bar mt-6" elevation="2">
      <div class="tv-action-bar-inner">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display: inline-block;">
                <v-btn
                  class="tv-action-btn tv-action-btn--verify"
                  @click="verifyWithExclaimScorer"
                  :loading="scoringInProgress"
                  :disabled="!isTreeComplete"
                >
                  <v-icon left>mdi-check-decagram</v-icon>
                  Verify with Exclaim Scorer
                </v-btn>
              </div>
            </template>
            <span>{{ exclaimScorerTooltip }}</span>
          </v-tooltip>

          <v-btn class="tv-action-btn tv-action-btn--export ml-3" @click="exportTree">
            <v-icon left>mdi-export</v-icon>
            Export
          </v-btn>

          <v-btn class="tv-action-btn tv-action-btn--json ml-3" @click="openInJsonView">
            <v-icon left>mdi-code-json</v-icon>
            JSON View
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Scoring Results Dialog -->
    <v-dialog v-model="scoringDialog" max-width="900px" scrollable>
      <v-card class="tv-dialog-card">
        <div
          class="tv-dialog-header tv-dialog-header--scoring"
          :style="{
            background:
              scoringHeaderColor === 'success'
                ? 'linear-gradient(135deg, #2e7d32, #43a047, #66bb6a)'
                : scoringHeaderColor === 'warning'
                ? 'linear-gradient(135deg, #e65100, #f57c00, #ff9800)'
                : 'linear-gradient(135deg, #c62828, #e53935, #ef5350)',
          }"
        >
          <v-icon color="white" class="mr-2" size="24">mdi-check-decagram</v-icon>
          <span class="white--text text-h6 font-weight-bold">Exclaim Scorer Results</span>
          <v-spacer></v-spacer>
          <v-chip dark color="rgba(255,255,255,0.25)" class="white--text font-weight-bold">
            Overall Score: {{ (overallScore * 100).toFixed(2) }}%
          </v-chip>
        </div>
        <v-card-text class="pt-4" style="max-height: 600px;">
          <div v-if="scoredData">
            <v-alert type="info" outlined class="mb-4">
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-information</v-icon>
                <div>
                  <strong>Model:</strong> {{ scoredData.model_name }} ({{ scoredData.model_type }})
                </div>
              </div>
            </v-alert>

            <!-- Claims with Scores -->
            <v-expansion-panels accordion multiple>
              <v-expansion-panel
                v-for="(claim, claimIndex) in scoredData.scored_json.claims"
                :key="claimIndex"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-clipboard-text-outline</v-icon>
                    <span class="font-weight-medium"
                      >Claim {{ claimIndex + 1 }}: {{ claim.claim }}</span
                    >
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Sub-claims -->
                  <v-expansion-panels accordion class="mt-2">
                    <v-expansion-panel
                      v-for="(subclaim, subclaimIndex) in claim.subclaims"
                      :key="subclaimIndex"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex align-center">
                          <v-icon color="secondary" small class="mr-2"
                            >mdi-format-list-checks</v-icon
                          >
                          <span class="text-body-2">{{ subclaim.subclaim }}</span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <!-- Evidence with Scores -->
                        <v-simple-table dense class="mt-2">
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th>Evidence Type</th>
                                <th>Description</th>
                                <th>Comprehensiveness</th>
                                <th>Sufficiency</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="(evidence, evidenceIndex) in subclaim.evidence"
                                :key="evidenceIndex"
                              >
                                <td class="font-weight-medium">{{ evidence.Type }}</td>
                                <td>{{ evidence.Description }}</td>
                                <td>
                                  <v-chip
                                    v-if="evidence.edge_scores && evidence.edge_scores.length > 0"
                                    small
                                    :color="
                                      getScoreColorFromValue(
                                        evidence.edge_scores[0].comprehensiveness_score,
                                      )
                                    "
                                    dark
                                  >
                                    {{
                                      (
                                        evidence.edge_scores[0].comprehensiveness_score * 100
                                      ).toFixed(2)
                                    }}%
                                  </v-chip>
                                  <span v-else class="text-caption grey--text">N/A</span>
                                </td>
                                <td>
                                  <v-chip
                                    v-if="evidence.edge_scores && evidence.edge_scores.length > 0"
                                    small
                                    :color="
                                      getScoreColorFromValue(
                                        evidence.edge_scores[0].sufficiency_score,
                                      )
                                    "
                                    dark
                                  >
                                    {{
                                      (evidence.edge_scores[0].sufficiency_score * 100).toFixed(2)
                                    }}%
                                  </v-chip>
                                  <span v-else class="text-caption grey--text">N/A</span>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="scoringDialog = false">Close</v-btn>
          <v-btn
            class="tv-action-btn tv-action-btn--save"
            @click="saveScoredResults"
            :loading="savingScores"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Scores to Claims
          </v-btn>
          <v-btn class="tv-action-btn tv-action-btn--verify" @click="downloadScoredJson">
            <v-icon left>mdi-download</v-icon>
            Download Scored JSON
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Claim Dialog -->
    <v-dialog v-model="addClaimDialog" max-width="600px">
      <v-card class="tv-dialog-card">
        <div class="tv-dialog-header tv-dialog-header--claim">
          <v-icon color="white" class="mr-2">mdi-clipboard-text-outline</v-icon>
          <span class="white--text text-h6 font-weight-bold">Add New Claim</span>
        </div>
        <v-card-text class="pt-4">
          <v-form ref="claimForm" v-model="claimFormValid">
            <v-text-field
              v-model="newClaim.title"
              label="Claim Title"
              :rules="[v => !!v || 'Title is required']"
              outlined
              dense
            ></v-text-field>

            <v-textarea
              v-model="newClaim.description"
              label="Description"
              hint="Provide a clear description for this claim"
              :rules="[v => !!v || 'Description is required']"
              auto-grow
              rows="3"
              outlined
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addClaimDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!claimFormValid" @click="addClaim">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Sub-Claim Dialog -->
    <v-dialog v-model="addSubClaimDialog" max-width="600px">
      <v-card class="tv-dialog-card">
        <div class="tv-dialog-header tv-dialog-header--subclaim">
          <v-icon color="white" class="mr-2">mdi-format-list-checks</v-icon>
          <span class="white--text text-h6 font-weight-bold">Add New Sub-Claim</span>
        </div>
        <v-card-text class="pt-4">
          <v-form ref="subClaimForm" v-model="subClaimFormValid">
            <v-text-field
              v-model="newSubClaim.title"
              label="Sub-Claim Title"
              :rules="[v => !!v || 'Title is required']"
              outlined
              dense
            ></v-text-field>

            <v-textarea
              v-model="newSubClaim.description"
              label="Description"
              hint="Provide a clear description for this sub-claim"
              :rules="[v => !!v || 'Description is required']"
              auto-grow
              rows="3"
              outlined
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addSubClaimDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :disabled="!subClaimFormValid" @click="addSubClaim">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Evidence Dialog -->
    <v-dialog v-model="addEvidenceDialog" max-width="600px">
      <v-card class="tv-dialog-card">
        <div class="tv-dialog-header tv-dialog-header--evidence">
          <v-icon color="white" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="white--text text-h6 font-weight-bold">Add New Evidence</span>
        </div>
        <v-card-text class="pt-4">
          <v-form ref="evidenceForm" v-model="evidenceFormValid">
            <v-text-field
              v-model="newEvidence.title"
              label="Evidence Title"
              :rules="[v => !!v || 'Title is required']"
              outlined
              dense
            ></v-text-field>

            <v-select
              v-model="newEvidence.type"
              :items="evidenceTypes"
              label="Evidence Type"
              :rules="[v => !!v || 'Type is required']"
              outlined
              dense
            ></v-select>

            <v-textarea
              v-model="newEvidence.description"
              label="Description"
              hint="Provide a clear description for this evidence"
              :rules="[v => !!v || 'Description is required']"
              auto-grow
              rows="3"
              outlined
            ></v-textarea>

            <v-file-input
              v-model="evidenceFile"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv"
              label="Upload Evidence File (Optional)"
              outlined
              dense
              show-size
              prepend-icon="mdi-file-upload"
              class="mt-2"
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addEvidenceDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :disabled="!evidenceFormValid"
            @click="addEvidence"
            :loading="savingEvidence"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog (generic) -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card v-if="itemToEdit" class="tv-dialog-card">
        <div
          class="tv-dialog-header"
          :class="
            'tv-dialog-header--' + (itemToEdit.type === 'subClaim' ? 'subclaim' : itemToEdit.type)
          "
        >
          <v-icon color="white" class="mr-2">{{ getNodeIcon(itemToEdit.type) }}</v-icon>
          <span class="white--text text-h6 font-weight-bold"
            >Edit {{ getNodeTypeLabel(itemToEdit.type) }}</span
          >
        </div>
        <v-card-text class="pt-4">
          <v-form ref="editForm" v-model="editFormValid">
            <v-text-field
              v-model="editedItem.name"
              label="Title"
              :rules="[v => !!v || 'Title is required']"
              outlined
              dense
            ></v-text-field>

            <v-select
              v-if="itemToEdit.type === 'evidence'"
              v-model="editedItem.type"
              :items="evidenceTypes"
              label="Evidence Type"
              :rules="[v => !!v || 'Type is required']"
              outlined
              dense
            ></v-select>

            <v-select
              v-if="itemToEdit.type === 'evidence'"
              v-model="editedItem.source"
              :items="sourceOptions"
              label="Evidence Source"
              :rules="[v => !!v || 'Source is required']"
              outlined
              dense
              hint="Select the source of this evidence"
              persistent-hint
            ></v-select>

            <v-textarea
              v-model="editedItem.description"
              label="Description"
              :rules="[v => !!v || 'Description is required']"
              auto-grow
              rows="3"
              outlined
            ></v-textarea>

            <!-- Add file upload or URL endpoint options for evidence -->
            <template v-if="itemToEdit.type === 'evidence'">
              <v-divider class="my-4"></v-divider>
              <div class="subtitle-1 mb-2">Evidence File/Endpoint</div>

              <v-text-field
                v-if="isToolType"
                v-model="editUrlEndpoint"
                label="Provide URL Endpoint"
                placeholder="Enter the URL endpoint for your tool integration"
                outlined
                dense
                hint="Update the endpoint URL for your tool integration"
                persistent-hint
              ></v-text-field>

              <v-file-input
                v-else
                v-model="editFile"
                :label="itemToEdit.fileName || 'Upload File'"
                outlined
                dense
                show-size
                prepend-icon="mdi-file-upload"
                hint="Upload a new file to replace the current one (optional)"
                persistent-hint
              ></v-file-input>

              <v-alert v-if="!isToolType" type="info" text dense class="mt-2">
                Leave empty to keep the current file
              </v-alert>
            </template>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!editFormValid"
            :loading="updateLoading"
            @click="updateItem"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import Loader from '@/components/helpers/Loader';
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import claimsApi from '../api/claimsApi';
import subClaimsApi from '../api/subClaimsApi';

export default {
  name: 'TreeView',
  components: {
    Breadcrumbs,
    Loader,
  },
  mixins: [util],
  data() {
    return {
      // Add missing properties referenced in template
      loading: false,
      items: [],
      openNodes: [],
      selectedNode: null,
      requirementTitle: '',
      requirementId: '',

      // Dialogs
      addClaimDialog: false,
      addSubClaimDialog: false,
      addEvidenceDialog: false,
      editDialog: false,

      // Form data
      claimFormValid: false,
      subClaimFormValid: false,
      evidenceFormValid: false,
      editFormValid: false,

      // New items data
      newClaim: { title: '', description: '' },
      newSubClaim: { title: '', description: '' },
      newEvidence: { title: '', description: '', type: '' },

      // Edit data
      itemToEdit: null,
      editedItem: { name: '', description: '', type: '' }, // Removed status

      // File upload
      evidenceFile: null,
      savingEvidence: false,

      // Evidence type options
      evidenceTypes: ['Document', 'Screenshot', 'Log', 'Test Result', 'Code Sample', 'Certificate'],

      // Source options
      sourceOptions: ['Manual', 'Threat Spider', 'Dependency Track', 'Threat Dragon JSON'],

      // Status options - We can keep this for other uses
      statusOptions: ['Draft', 'Pending', 'Completed'],

      requirement: null,

      // Add parent references for dialogs
      currentParentItem: null,

      // Add these new properties
      editFile: null,
      editUrlEndpoint: '',
      updateLoading: false,

      // Add new properties for scoring
      scoringDialog: false,
      scoringInProgress: false,
      scoredData: null,

      // Add new property
      savingScores: false,
    };
  },
  computed: {
    // Add computed property to check if selected evidence source is a tool type
    isToolType() {
      return (
        this.editedItem.source === 'Threat Spider' ||
        this.editedItem.source === 'Dependency Track' ||
        this.editedItem.source === 'Threat Dragon JSON'
      );
    },

    // Add new computed property to validate SAC completeness
    isTreeComplete() {
      if (!this.items || this.items.length === 0) return false;

      const rootNode = this.items.find(item => item.type === 'root');
      if (!rootNode || !rootNode.children || rootNode.children.length === 0) return false;

      // Check if at least one claim has a complete structure (claim -> sub-claim -> evidence)
      return rootNode.children.some(claim => {
        if (!claim.children || claim.children.length === 0) return false;

        // Check if at least one sub-claim has evidence
        return claim.children.some(subClaim => {
          return subClaim.children && subClaim.children.length > 0;
        });
      });
    },

    // Add computed property for the tooltip message
    exclaimScorerTooltip() {
      if (this.isTreeComplete) {
        return 'Verify your Security Assurance Case with Exclaim Scorer';
      }
      return 'Complete at least one Claim-Subclaim-Evidence structure to enable scoring';
    },

    // Add new computed property for overall score
    overallScore() {
      if (!this.scoredData || !this.scoredData.scored_json || !this.scoredData.scored_json.claims) {
        return 0;
      }

      let totalComprehensiveness = 0;
      let totalSufficiency = 0;
      let evidenceCount = 0;

      this.scoredData.scored_json.claims.forEach(claim => {
        if (claim.subclaims) {
          claim.subclaims.forEach(subclaim => {
            if (subclaim.evidence) {
              subclaim.evidence.forEach(evidence => {
                if (evidence.edge_scores && evidence.edge_scores.length > 0) {
                  const scores = evidence.edge_scores[0];
                  totalComprehensiveness += scores.comprehensiveness_score || 0;
                  totalSufficiency += scores.sufficiency_score || 0;
                  evidenceCount++;
                }
              });
            }
          });
        }
      });

      if (evidenceCount === 0) return 0;

      // Calculate average of both scores
      const avgComprehensiveness = totalComprehensiveness / evidenceCount;
      const avgSufficiency = totalSufficiency / evidenceCount;
      const overallAverage = (avgComprehensiveness + avgSufficiency) / 2;

      return overallAverage;
    },

    // Add computed property for header color class
    scoringHeaderColor() {
      //const scorePercentage = this.overallScore * 100;
      return this.getScoreColorFromValue(this.overallScore);
    },
  },
  created() {
    this.loading = true;

    // Check if we have a requirement in localStorage
    const storedRequirement = localStorage.getItem('inScopeRequirement');
    if (storedRequirement) {
      try {
        this.requirement = JSON.parse(storedRequirement);
        this.requirementTitle = this.requirement.title || 'Requirement';
        this.requirementId = this.requirement.id || '';
        this.fetchClaimsTree();
      } catch (e) {
        console.error('Error parsing requirement from localStorage:', e);
        EventBus.$emit('snackbar', {
          text: 'Failed to load requirement data',
          color: 'error',
        });
      }
    }

    this.loading = false;
  },
  methods: {
    // Expand all nodes in the tree
    expandAll() {
      // Find all possible node IDs
      const allIds = this.getAllNodeIds(this.items);
      this.openNodes = [...allIds];
    },

    // Collapse all nodes in the tree
    collapseAll() {
      this.openNodes = [];
    },

    // Get all node IDs recursively for expandAll function
    getAllNodeIds(nodes) {
      let ids = [];
      if (!nodes || nodes.length === 0) return ids;

      nodes.forEach(node => {
        if (node.id) ids.push(node.id);
        if (node.children && node.children.length > 0) {
          ids = [...ids, ...this.getAllNodeIds(node.children)];
        }
      });

      return ids;
    },

    // Handle node selection in the tree
    nodeSelected(selectedNodes) {
      if (selectedNodes && selectedNodes.length > 0) {
        // Find the selected node in the tree
        const nodeId = selectedNodes[0];
        this.selectedNode = this.findNodeById(this.items, nodeId);
      } else {
        this.selectedNode = null;
      }
    },

    // Find a node in the tree by ID (recursive)
    findNodeById(nodes, id) {
      if (!nodes) return null;

      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = this.findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },

    // Get node type label
    getNodeTypeLabel(type) {
      const labels = {
        root: 'Root',
        claim: 'Claim',
        subClaim: 'Sub-Claim',
        evidence: 'Evidence',
      };
      return labels[type] || type;
    },

    // Get node icon based on type
    getNodeIcon(type) {
      const icons = {
        root: 'mdi-file-tree',
        claim: 'mdi-clipboard-text-outline',
        subClaim: 'mdi-format-list-checks',
        evidence: 'mdi-file-document-outline',
      };
      return icons[type] || 'mdi-help-circle';
    },

    // Get status color for chips
    getStatusColor(status) {
      switch (status) {
        case 'Draft':
          return 'orange';
        case 'Pending':
          return 'blue';
        case 'Completed':
          return 'success';
        default:
          return 'grey';
      }
    },

    // Get source color for chips
    getSourceColor(source) {
      const colors = {
        Manual: 'blue',
        'Threat Spider': 'purple',
        'Dependency Track': 'orange',
        'Threat Dragon JSON': 'teal',
      };
      return colors[source] || 'grey';
    },

    // Get edit header class based on item type
    getEditHeaderClass(type) {
      switch (type) {
        case 'claim':
          return 'primary';
        case 'subClaim':
          return 'secondary';
        case 'evidence':
          return 'success';
        default:
          return 'info';
      }
    },

    // Add new method to check if a claim has Exclaim scores
    hasExclaimScores(node) {
      return (
        node.comprehensivenessScore !== null &&
        node.comprehensivenessScore !== undefined &&
        node.sufficiencyScore !== null &&
        node.sufficiencyScore !== undefined
      );
    },

    // Fetch claims tree data
    async fetchClaimsTree() {
      this.loading = true;
      try {
        if (!this.requirement || !this.requirement.id) {
          this.items = [];
          return;
        }

        // Start building the tree with a root node
        const rootNode = {
          id: 'root',
          name: this.requirementTitle || 'Security Assurance Case',
          type: 'root',
          children: [],
        };

        // Fetch claims for this requirement
        const { data: claims } = await claimsApi.all(this.requirement.id);

        // Process each claim
        for (const claim of claims) {
          const claimNode = {
            id: claim.id,
            name: claim.title,
            description: claim.description,
            status: claim.status,
            type: 'claim',
            createdAt: claim.createdAt,
            updatedAt: claim.updatedAt,
            username: claim.username,
            // Add Exclaim Scorer fields
            comprehensivenessScore: claim.comprehensivenessScore,
            sufficiencyScore: claim.sufficiencyScore,
            score: claim.score,
            scoringPremise: claim.scoringPremise,
            scoredAt: claim.scoredAt,
            children: [],
          };

          // Fetch sub-claims for this claim
          try {
            const { data: subClaims } = await subClaimsApi.all(claim.id);
            claimNode.childCount = subClaims.length;

            // Process each sub-claim
            for (const subClaim of subClaims) {
              const subClaimNode = {
                id: subClaim.id,
                name: subClaim.title,
                description: subClaim.description,
                status: subClaim.status,
                type: 'subClaim',
                createdAt: subClaim.createdAt,
                updatedAt: subClaim.updatedAt,
                username: subClaim.username,
                children: [],
              };

              // Add evidences to sub-claim if they exist
              if (subClaim.evidences && subClaim.evidences.length > 0) {
                subClaimNode.childCount = subClaim.evidences.length;

                subClaim.evidences.forEach(evidence => {
                  const evidenceNode = {
                    id: evidence.id,
                    name: evidence.title,
                    description: evidence.description,
                    status: evidence.status,
                    type: 'evidence',
                    evidenceType: evidence.type,
                    source: evidence.source,
                    createdAt: evidence.createdAt,
                    updatedAt: evidence.updatedAt,
                    username: evidence.username,
                    fileName: evidence.fileName,
                    version: evidence.version,
                  };

                  subClaimNode.children.push(evidenceNode);
                });
              }

              claimNode.children.push(subClaimNode);
            }
          } catch (error) {
            console.error('Error fetching sub-claims:', error);
          }

          rootNode.children.push(claimNode);
        }

        this.items = [rootNode];

        // Open the root node by default
        if (rootNode.id && !this.openNodes.includes(rootNode.id)) {
          this.openNodes.push(rootNode.id);
        }
      } catch (error) {
        console.error('Error fetching claims tree:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to load security assurance case data',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    // Open dialog to add a new claim
    openAddClaimDialog(parentItem) {
      this.currentParentItem = parentItem;
      this.newClaim = { title: '', description: '' };
      this.addClaimDialog = true;
    },

    // Open dialog to add a new sub-claim
    openAddSubClaimDialog(parentItem) {
      this.currentParentItem = parentItem;
      this.newSubClaim = { title: '', description: '' };
      this.addSubClaimDialog = true;
    },

    // Open dialog to add new evidence
    openAddEvidenceDialog(parentItem) {
      this.currentParentItem = parentItem;
      this.newEvidence = { title: '', description: '', type: '' };
      this.addEvidenceDialog = true;
    },

    // Open dialog to edit item
    openEditDialog(item) {
      this.itemToEdit = item;
      this.editedItem = {
        name: item.name,
        description: item.description,
        type: item.evidenceType || '',
        source: item.source || 'Manual',
      };

      // Initialize file/URL fields for evidence
      if (item.type === 'evidence') {
        this.editFile = null;

        // If it's a tool type evidence and has a fileName that's not 'null',
        // use it as URL endpoint
        if (this.isToolType && item.fileName && item.fileName !== 'null') {
          this.editUrlEndpoint = item.fileName;
        } else {
          this.editUrlEndpoint = '';
        }
      }

      this.editDialog = true;
    },

    // New method to update an item
    async updateItem() {
      if (!this.editFormValid) return;

      this.updateLoading = true;
      try {
        const itemType = this.itemToEdit.type;

        if (itemType === 'claim') {
          // Update claim
          const claimPayload = {
            id: this.itemToEdit.id,
            title: this.editedItem.name,
            description: this.editedItem.description,
            status: this.itemToEdit.status,
          };

          await claimsApi.update(claimPayload);
        } else if (itemType === 'subClaim') {
          // Update sub-claim
          const subClaimPayload = {
            id: this.itemToEdit.id,
            title: this.editedItem.name,
            description: this.editedItem.description,
            status: this.itemToEdit.status,
            evidences:
              this.itemToEdit.children?.map(evidence => ({
                id: evidence.id,
                title: evidence.name,
                description: evidence.description,
                status: evidence.status,
                type: evidence.evidenceType,
              })) || [],
          };

          await subClaimsApi.update(subClaimPayload);
        } else if (itemType === 'evidence') {
          // Update evidence - with file handling
          const evidenceApi = await import('@/modules/evidence/api/evidenceApi').then(
            m => m.default,
          );
          const formData = new FormData();

          formData.append('id', this.itemToEdit.id);
          formData.append('title', this.editedItem.name);
          formData.append('description', this.editedItem.description);
          formData.append('type', this.editedItem.type);
          formData.append('source', this.editedItem.source || 'Manual');
          formData.append('version', this.itemToEdit.version || 'v1.0');

          // Handle file or URL endpoint based on evidence type
          if (this.isToolType) {
            // For tool types, use URL as fileName
            formData.append('fileName', this.editUrlEndpoint || 'null');
          } else if (this.editFile) {
            // If a new file is provided, upload it
            formData.append('file', this.editFile);
            formData.append('fileName', this.editFile.name);
          } else {
            // Keep the existing file
            formData.append('fileName', this.itemToEdit.fileName || 'null');
          }

          await evidenceApi.update(formData);
        }

        EventBus.$emit('snackbar', {
          text: `${this.getNodeTypeLabel(itemType)} updated successfully`,
          color: 'success',
        });

        this.editDialog = false;
        await this.fetchClaimsTree(); // Refresh the tree data
      } catch (error) {
        console.error('Error updating item:', error);
        EventBus.$emit('snackbar', {
          text: `Failed to update ${this.getNodeTypeLabel(this.itemToEdit.type)}: ${error.message ||
            'Unknown error'}`,
          color: 'error',
        });
      } finally {
        this.updateLoading = false;
      }
    },

    // Confirm delete for an item
    confirmDelete(item) {
      // This would typically open a confirmation dialog
      if (confirm(`Are you sure you want to delete this ${item.type}?`)) {
        // Delete logic would go here
      }
    },

    // Add a new claim
    async addClaim() {
      if (!this.claimFormValid) return;

      this.loading = true;
      try {
        // Create claim payload using the parent reference
        const claimPayload = {
          title: this.newClaim.title,
          description: this.newClaim.description,
          socRequirementLink: this.requirement,
        };

        // Actually use the payload by making the API call
        await claimsApi.create(claimPayload);

        await this.fetchClaimsTree(); // Refresh tree data
        this.addClaimDialog = false;
      } catch (error) {
        console.error('Error adding claim:', error);
      } finally {
        this.loading = false;
      }
    },

    // Add a new sub-claim
    async addSubClaim() {
      if (!this.subClaimFormValid) return;

      this.loading = true;
      try {
        // Create sub-claim payload with the correct structure the API expects
        const subClaimPayload = {
          title: this.newSubClaim.title,
          description: this.newSubClaim.description,
          // Change from claimLink to claim and send just the ID or a properly structured object
          claim: {
            id: this.currentParentItem.id,
          },
        };

        // Actually use the payload by making the API call
        await subClaimsApi.create(subClaimPayload);

        EventBus.$emit('snackbar', {
          text: 'Sub-claim created successfully',
          color: 'success',
        });

        await this.fetchClaimsTree(); // Refresh tree data
        this.addSubClaimDialog = false;
      } catch (error) {
        console.error('Error adding sub-claim:', error);
        EventBus.$emit('snackbar', {
          text:
            'Failed to create sub-claim: ' +
            (error.response?.data?.message || error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    // Add new evidence
    async addEvidence() {
      if (!this.evidenceFormValid) return;

      this.savingEvidence = true;
      try {
        // Create a FormData object instead of a JSON object
        const formData = new FormData();

        // Append all the evidence data as form fields
        formData.append('title', this.newEvidence.title);
        formData.append('description', this.newEvidence.description);
        formData.append('type', this.newEvidence.type);
        formData.append('version', 'v1.0');

        // Add the subClaim ID - ensure it's properly formatted
        const subClaimData = { id: this.currentParentItem.id };
        formData.append('subClaim', JSON.stringify(subClaimData));

        // Add the file if one was selected
        if (this.evidenceFile) {
          formData.append('file', this.evidenceFile);
          formData.append('fileName', this.evidenceFile.name);
        } else {
          formData.append('fileName', 'null');
        }

        // Import the evidenceApi and make the API call
        const evidenceApi = await import('@/modules/evidence/api/evidenceApi').then(m => m.default);
        await evidenceApi.create(formData);

        EventBus.$emit('snackbar', {
          text: 'Evidence created successfully and linked to sub-claim',
          color: 'success',
        });

        await this.fetchClaimsTree(); // Refresh tree data
        this.addEvidenceDialog = false;
      } catch (error) {
        console.error('Error adding evidence:', error);
        EventBus.$emit('snackbar', {
          text:
            'Failed to create evidence: ' +
            (error.response?.data?.message || error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        this.savingEvidence = false;
      }
    },

    // Export tree data
    exportTree() {
      try {
        // Create a clean copy of the tree data without Vue-specific properties
        const cleanData = this.prepareTreeDataForExport(this.items);

        // Convert to formatted JSON string
        const jsonString = JSON.stringify(cleanData, null, 2);

        // Create a Blob containing the JSON data
        const blob = new Blob([jsonString], { type: 'application/json' });

        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);

        // Create a download link
        const link = document.createElement('a');
        link.href = url;

        // Set the filename with a timestamp to avoid duplicates
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const requirementIdSafe = this.requirementId ? `-${this.requirementId}` : '';
        link.download = `security-assurance-case${requirementIdSafe}-${timestamp}.json`;

        // Append to the document, click it, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        URL.revokeObjectURL(url);

        EventBus.$emit('snackbar', {
          text: 'Tree data exported successfully as JSON file',
          color: 'success',
        });
      } catch (error) {
        console.error('Error exporting tree data:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to export tree data: ' + error.message,
          color: 'error',
        });
      }
    },

    // Helper method to clean up tree data for export
    prepareTreeDataForExport(items) {
      if (!items || !Array.isArray(items)) return [];

      // Find the root node and process its children (claims)
      const rootNode = items.find(item => item.type === 'root');
      if (!rootNode || !rootNode.children) return { Claims: [] };

      const claims = rootNode.children.map(claimItem => {
        const claim = {
          Claim: claimItem.name,
          Subclaims: [],
        };

        // Process sub-claims
        if (claimItem.children && claimItem.children.length > 0) {
          claim.Subclaims = claimItem.children.map(subClaimItem => {
            const subclaim = {
              Subclaim: subClaimItem.name,
              Evidence: [],
            };

            // Process evidence
            if (subClaimItem.children && subClaimItem.children.length > 0) {
              subclaim.Evidence = subClaimItem.children.map(evidenceItem => ({
                Type: evidenceItem.evidenceType || evidenceItem.type || 'General Evidence',
                Description: evidenceItem.description || evidenceItem.name,
              }));
            }

            return subclaim;
          });
        }

        return claim;
      });

      return { Claims: claims };
    },

    // Add this new method to open the tree data in JSON View
    openInJsonView() {
      try {
        // Prepare the tree data (same as in exportTree method)
        const cleanData = this.prepareTreeDataForExport(this.items);

        // Convert to formatted JSON string
        const jsonString = JSON.stringify(cleanData, null, 2);

        // Store in localStorage for the JSON view to access
        localStorage.setItem('treeViewJsonData', jsonString);

        // Navigate to JSON View
        this.$router.push({ name: 'JsonView' });
      } catch (error) {
        console.error('Error preparing data for JSON view:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to prepare data for JSON view: ' + error.message,
          color: 'error',
        });
      }
    },

    async verifyWithExclaimScorer() {
      this.scoringInProgress = true;

      try {
        // Prepare the tree data in the correct format
        const cleanData = this.prepareTreeDataForExport(this.items);

        // Transform to the format expected by Exclaim Scorer
        const exclaimPayload = {
          model_type: 'encoder',
          model_name: 'bert-large-uncased',
          task: null,
          assurance_case_json: {
            claims: cleanData.Claims.map(claim => ({
              claim: claim.Claim,
              subclaims: claim.Subclaims.map(subclaim => ({
                subclaim: subclaim.Subclaim,
                evidence: subclaim.Evidence.map(ev => ({
                  Type: ev.Type,
                  Description: ev.Description,
                })),
              })),
            })),
          },
        };

        // Import the scoring API
        const exclaimScorerApi = await import('@/modules/sac/api/exclaimScorerApi').then(
          m => m.default,
        );

        // Send to backend
        const response = await exclaimScorerApi.generateScores(exclaimPayload);

        if (response && response.data) {
          this.scoredData = response.data;
          this.scoringDialog = true;

          EventBus.$emit('snackbar', {
            text: 'Scoring completed successfully',
            color: 'success',
          });
        }
      } catch (error) {
        console.error('Error verifying with Exclaim Scorer:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to verify with Exclaim Scorer: ' + (error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        this.scoringInProgress = false;
      }
    },

    getScoreColorFromValue(score) {
      // Convert score (0-1) to percentage and color code
      const percentage = score * 100;
      if (percentage >= 75) {
        return 'success';
      } else if (percentage >= 50) {
        return 'warning';
      } else if (percentage >= 25) {
        return 'orange';
      } else {
        return 'error';
      }
    },

    downloadScoredJson() {
      try {
        const jsonString = JSON.stringify(this.scoredData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        link.download = `scored-assurance-case-${timestamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        EventBus.$emit('snackbar', {
          text: 'Scored JSON downloaded successfully',
          color: 'success',
        });
      } catch (error) {
        console.error('Error downloading scored JSON:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to download scored JSON',
          color: 'error',
        });
      }
    },

    async saveScoredResults() {
      if (!this.scoredData) {
        EventBus.$emit('snackbar', {
          text: 'No scored data to save',
          color: 'warning',
        });
        return;
      }

      this.savingScores = true;

      try {
        const securityAssuranceCaseApi = await import('../api/securityAssuranceCaseApi').then(
          m => m.default,
        );

        // Convert to JSON string if it's not already
        const jsonString =
          typeof this.scoredData === 'string' ? this.scoredData : JSON.stringify(this.scoredData);

        await securityAssuranceCaseApi.saveExclaimScores(jsonString);

        EventBus.$emit('snackbar', {
          text: 'Scores saved successfully to claim records',
          color: 'success',
        });

        // Refresh the tree to show updated scores
        await this.fetchClaimsTree();

        this.scoringDialog = false;
      } catch (error) {
        console.error('Error saving scores:', error);
        EventBus.$emit('snackbar', {
          text: 'Failed to save scores: ' + (error.message || 'Unknown error'),
          color: 'error',
        });
      } finally {
        this.savingScores = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   TreeView - Modern UI/UX Styles
   ======================================== */

/* Main Container */
.tv-main-container {
  padding: 24px 32px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
}

.tv-breadcrumbs-container {
  margin-bottom: 20px;
}

/* Back Button */
.tv-back-btn {
  margin-bottom: 20px;
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0 20px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.tv-back-btn:hover {
  transform: translateX(-3px);
  box-shadow: 0 4px 15px rgba(26, 35, 126, 0.2) !important;
}

/* ========================================
   Hero Guide Card
   ======================================== */
.tv-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 24px;
  border: none !important;
}

.tv-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 28px 32px;
}

.tv-hero-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tv-hero-logo {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.tv-hero-title {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.tv-hero-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-top: 4px;
}

.tv-guide-body {
  padding: 24px 28px;
}

/* Info Cards in Hero */
.tv-info-card {
  background: linear-gradient(135deg, #fafbfd 0%, #f0f2f5 100%);
  border-radius: 14px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  height: 100%;
}
.tv-info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.tv-info-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}
.tv-info-icon-wrapper.nav-icon {
  background: linear-gradient(135deg, #1565c0, #42a5f5);
}
.tv-info-icon-wrapper.manage-icon {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
}

.tv-info-title {
  font-size: 1rem;
  font-weight: 700;
  color: #263238;
  margin-bottom: 10px;
}

.tv-info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.tv-info-list li {
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 0.875rem;
  color: #546e7a;
  line-height: 1.5;
}

/* Tips Bar */
.tv-tips-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #fff8e1, #fff3e0);
  border-radius: 12px;
  border: 1px solid rgba(255, 160, 0, 0.15);
}

/* ========================================
   Tree Header Card
   ======================================== */
.tv-tree-header-card {
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 24px;
  border: none !important;
}

.tv-tree-header-gradient {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tv-toolbar-btn {
  text-transform: none;
  letter-spacing: 0.3px;
  border-radius: 10px !important;
  font-weight: 500;
  margin-left: 8px;
  transition: all 0.2s ease !important;
}
.tv-toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
}

/* ========================================
   Tree Card (Main Tree Panel)
   ======================================== */
.tv-tree-card {
  border-radius: 16px !important;
  overflow: hidden;
  min-height: 400px;
  border: none !important;
}

.tv-tree-card-header {
  background: linear-gradient(135deg, #1565c0, #1e88e5, #42a5f5);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
}

/* Modern Tree View */
.tv-modern-treeview {
  padding: 20px;
}

/* Node Icon Wrappers - Color coded by type */
.tv-node-icon-wrapper {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.tv-node-icon--root {
  background: linear-gradient(135deg, #37474f, #546e7a);
}
.tv-node-icon--claim {
  background: linear-gradient(135deg, #1565c0, #42a5f5);
}
.tv-node-icon--subClaim {
  background: linear-gradient(135deg, #7b1fa2, #ab47bc);
}
.tv-node-icon--evidence {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
}

/* Tree Node Rows */
.tv-modern-treeview >>> .v-treeview-node__root {
  padding: 6px 8px;
  border-radius: 12px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}
.tv-modern-treeview >>> .v-treeview-node__root:hover {
  background: linear-gradient(135deg, #e3f2fd, #f3f8ff) !important;
  border-color: rgba(21, 101, 192, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}
.tv-modern-treeview >>> .v-treeview-node__root--active {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
  border-color: rgba(21, 101, 192, 0.25);
}

.tree-node {
  width: 100%;
  padding: 6px 0;
}

.tree-item-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #263238;
}

.tree-item-description {
  max-width: 450px;
  color: #78909c;
  font-size: 0.8rem;
  margin-top: 2px;
}

.tree-actions {
  display: flex;
  gap: 2px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}
.tree-node:hover .tree-actions {
  opacity: 1;
}

/* ========================================
   Detail Card (Side Panel)
   ======================================== */
.tv-detail-card {
  border-radius: 16px !important;
  overflow: hidden;
  position: sticky;
  top: 24px;
  border: none !important;
}

.tv-detail-header {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
}
.tv-detail-header--claim {
  background: linear-gradient(135deg, #1565c0, #42a5f5);
}
.tv-detail-header--subClaim {
  background: linear-gradient(135deg, #7b1fa2, #ab47bc);
}
.tv-detail-header--evidence {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
}
.tv-detail-header--root {
  background: linear-gradient(135deg, #37474f, #546e7a);
}
.tv-detail-header--empty {
  background: linear-gradient(135deg, #78909c, #90a4ae);
}

.tv-detail-item {
  margin-bottom: 14px;
}

.tv-detail-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #78909c;
  margin-bottom: 4px;
}

.tv-detail-value {
  font-size: 0.9rem;
  color: #37474f;
  line-height: 1.5;
}

/* Scoring Metrics in Detail Panel */
.tv-scoring-metrics {
  background: linear-gradient(135deg, #f3f8ff, #e8f0fe);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(21, 101, 192, 0.1);
}

.tv-score-circle {
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15));
}

.tv-edit-btn {
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: all 0.3s ease !important;
}
.tv-edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
}

/* ========================================
   Action Bar
   ======================================== */
.tv-action-bar {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.tv-action-bar-inner {
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: linear-gradient(135deg, #fafbfd, #f0f4f8);
}

.tv-action-btn {
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0 20px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  color: white !important;
}
.tv-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

.tv-action-btn--verify {
  background: linear-gradient(135deg, #2e7d32, #66bb6a) !important;
}
.tv-action-btn--export {
  background: linear-gradient(135deg, #0277bd, #29b6f6) !important;
}
.tv-action-btn--json {
  background: linear-gradient(135deg, #1a237e, #3949ab) !important;
}
.tv-action-btn--save {
  background: linear-gradient(135deg, #0277bd, #29b6f6) !important;
}

.tv-action-btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================
   Dialog Cards
   ======================================== */
.tv-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.tv-dialog-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
}
.tv-dialog-header--claim {
  background: linear-gradient(135deg, #1565c0, #1e88e5, #42a5f5);
}
.tv-dialog-header--subclaim {
  background: linear-gradient(135deg, #7b1fa2, #9c27b0, #ab47bc);
}
.tv-dialog-header--evidence {
  background: linear-gradient(135deg, #2e7d32, #388e3c, #66bb6a);
}
.tv-dialog-header--root {
  background: linear-gradient(135deg, #37474f, #455a64, #546e7a);
}
.tv-dialog-header--scoring {
  /* Dynamic gradient applied via inline style */
  padding: 20px 24px;
}

/* ========================================
   Responsive Tweaks
   ======================================== */
@media (max-width: 1264px) {
  .tv-detail-card {
    position: relative;
    top: 0;
    margin-top: 16px;
  }
}

@media (max-width: 960px) {
  .tv-main-container {
    padding: 16px;
  }
  .tv-hero-header {
    padding: 20px;
  }
  .tv-tips-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  .tv-tree-header-gradient {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>
